import React, { useEffect, useId, useState } from 'react';

import { useSetupTarget } from '../../onboarding/useSetupTarget';

/**
 * The collapsible section shell shared by every card on the Primary Admin and
 * Master Admin pages. Extracted from MasterAdminSettings.jsx (which re-exports
 * it for existing importers) so sections rendered INSIDE that file can import
 * the shell without a circular module dependency.
 *
 * Disclosure semantics (M65A): the trigger reports aria-expanded and, while
 * open, points at the body via aria-controls; the body is a region labelled by
 * the visible title. The body stays conditionally rendered on purpose —
 * section bodies fetch on mount, so mounting them closed would fire their
 * backend calls for every collapsed section.
 *
 * `anchor` makes a section addressable as ?section=<anchor>. The setup
 * checklist links straight to the panel a step is about: without it a merchant
 * arrived at a page of collapsed panels with no idea which one was meant.
 */
export function MASection({ title, icon, anchor, defaultOpen = false, headerExtra, children }) {
  const { targeted, ref } = useSetupTarget('section', anchor);
  const [open, setOpen] = useState(defaultOpen);
  const uid = useId().replace(/:/g, '');
  const labelId = `${uid}-label`;
  const bodyId = `${uid}-body`;

  // Opening is this component's business; the hook only says "you're wanted".
  // Deliberately one-way: it opens a targeted section and never closes one the
  // merchant opened themselves.
  useEffect(() => {
    if (targeted) setOpen(true);
  }, [targeted]);

  return (
    <div className="ma-section" id={anchor} ref={ref} data-setup-target={targeted ? 'true' : undefined}>
      <button
        type="button"
        className="ma-section__trigger"
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ma-section__icon" aria-hidden="true">{icon}</span>
        <span className="ma-section__label" id={labelId}>{title}</span>
        {headerExtra && <span className="ma-section__extra">{headerExtra}</span>}
        <span className="ma-section__btn" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="ma-section__body" id={bodyId} role="region" aria-labelledby={labelId}>
          {children}
        </div>
      )}
    </div>
  );
}

export default MASection;
